import { useState, useEffect, useMemo, useCallback } from 'react';
import AddressClient from 'src/services/AddressClient';
import { Address } from 'src/types/address';

interface UseShippingAddressParams {
  authProvider: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  stack?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  shippingAddressId?: string;
}

interface UseShippingAddressResult {
  shippingAddress: Address | null;
  loading: boolean;
  error: Error | null;
  called: boolean;
  fetchAddress: () => void;
}

export const useShippingAddress = ({
  authProvider,
  stack,
  shippingAddressId
}: UseShippingAddressParams): UseShippingAddressResult => {
  const [shippingAddress, setShippingAddress] = useState<Address | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [called, setCalled] = useState<boolean>(false);

  const addressClient = useMemo(
    () => new AddressClient(authProvider, stack),
    [authProvider, stack]
  );

  const fetchAddress = useCallback(async () => {
    if (!authProvider || !shippingAddressId || called) return;

    setLoading(true);
    setError(null);
    setCalled(true);

    try {
      const data = await addressClient.getAddressById(shippingAddressId);
      setShippingAddress(data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [authProvider, shippingAddressId, addressClient, called]);

  useEffect(() => {
    if (shippingAddressId && !called) {
      fetchAddress();
    }
  }, [fetchAddress, shippingAddressId, called]);

  return {
    shippingAddress,
    loading,
    error,
    called,
    fetchAddress
  };
};

export default useShippingAddress;
